"use client";

import Carousel from "@/components/Carousel";
import CarouselDots from "@/components/Carousel/CarouselDots";
import CarouselProvider from "@/components/Carousel/CarouselProvider";
import Image from "@/components/Image";
import useMediaQuery from "@/hooks/useMediaQuery";
import classNames from "@/helpers/classNames";
import styles from "./styles.module.scss";

interface GalleryImage {
  url: string;
  alt?: string;
}

export interface ProductGalleryProps {
  mainImage?: GalleryImage;
  images?: GalleryImage[];
  className?: string;
}

const ProductGallery = ({ mainImage, images = [], className }: ProductGalleryProps) => {
  const isMobile = useMediaQuery({ down: "largeTablet" });

  if (!mainImage && images.length === 0) {
    return null;
  }

  const allImages = [...(mainImage ? [mainImage] : []), ...images];

  if (isMobile) {
    return (
      <div className={classNames(styles.container, className)}>
        <CarouselProvider>
          <Carousel
            className={styles.mobileCarousel}
            containerClassName={styles.mobileCarouselContainer}
            options={{ align: "start", containScroll: "trimSnaps" }}
          >
            {allImages.map((image, index) => (
              <div key={index} className={styles.mobileSlide}>
                <Image
                  src={image.url}
                  alt={image.alt || ""}
                  fill
                  sizes="100vw"
                  priority={index === 0}
                  className={styles.mobileImage}
                />
              </div>
            ))}
          </Carousel>
          {allImages.length > 1 && (
            <CarouselDots className={styles.mobileDots} theme="dark" dotSize="xs" />
          )}
        </CarouselProvider>
      </div>
    );
  }

  const secondaryImages = images.slice(0, 2);
  const remainingImages = images.slice(2);

  return (
    <div className={classNames(styles.container, className)}>
      {mainImage && (
        <Image
          src={mainImage.url}
          alt={mainImage.alt || ""}
          fill
          sizes="(max-width: 1025px) 100vw, 50vw"
          priority
          className={styles.mainImage}
        />
      )}

      {secondaryImages.length > 0 && (
        <div className={styles.secondaryGrid}>
          {secondaryImages.map((image, index) => (
            <Image
              key={index}
              src={image.url}
              alt={image.alt || ""}
              fill
              sizes="(max-width: 1025px) 50vw, 25vw"
              className={styles.secondaryImage}
            />
          ))}
        </div>
      )}

      {remainingImages.map((image, index) => (
        <Image
          key={index}
          src={image.url}
          alt={image.alt || ""}
          fill
          sizes="(max-width: 1025px) 100vw, 50vw"
          className={styles.fullWidthImage}
        />
      ))}
    </div>
  );
};

export default ProductGallery;
